import { useEffect, useMemo, useState } from "react"
import { DirectArchiveModal, Labels, useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { Action, DetailFieldProps, DetailPanel, FieldType, FieldValue, ModalId, showErrorStrToast, showToast, StaticValue, UIPermission, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { define } from "../../../../../utils/typeUtils"
import { CreditPayment, PaymentOrderStatus } from "../../../state/types"
import { CreditPaymentOperationItemsTable } from "./creditPaymentOperationItemsTable"
import { DownloadReceiptModal } from "../../../../common"
import { UploadCreditReceiptFormModal } from "../upload-credit-receipt-modal/uploadCreditReceiptFormModal"
import { DownloadPaymentReceiptPDFModal } from "../../payment-receipt/download-payment-receipt-pdf-modal/downloadPaymentReceiptPDFModal"
import { usePriceOutlineHooks, usePricesHooks } from "../../../../operations"
import { usePaymentTypeLoaders } from "../../../loaders/paymentTypeLoaders"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { creditPaymentHelpers, useCreditPaymentHooks } from "../../../hooks/creditPaymentHooks"
import { useCreditPaymentService } from "../../../services/creditPaymentService"
import { SaleOperation, saleOperationHelpers, useSaleOperationLoaders } from "../../../../sales"
import { AnnulCreditPaymentModal } from "../annul-credit-payment-modal/annulCreditPaymentModal"

export const CreditPaymentDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const creditPayment = dataType! as CreditPayment
  const { operationItems, ...data } = creditPayment

  const [operations, setOperations] = useState<SaleOperation[]>([])
  const [accreditationMissing, setAccreditationMissing] = useState(false)
  const [retryingAccreditation, setRetryingAccreditation] = useState(false)

  const { isArchivable, isAnnullable } = creditPaymentHelpers
  const { getTable } = saleOperationHelpers
  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_CREDIT_PAYMENTS) || creditPayment.archived
  const { describe } = useDescribeHooks()
  const { formatDateTime } = useDataTypesHooks()
  const pricesHooks = usePricesHooks()
  const { getPriceOutline } = usePriceOutlineHooks()

  const paymentTypeLoaders = usePaymentTypeLoaders()
  const saleOperationLoaders = useSaleOperationLoaders()

  const { isAccreditationMissing } = useCreditPaymentHooks()
  const { retryAccreditation } = useCreditPaymentService()

  const { openModal } = useUIActions()

  useEffect(() => {
    const operationIds = creditPayment.operationItems.map(operationItem => operationItem.operationId)
    saleOperationLoaders().loadSaleOperations(operationIds).then(setOperations)
    isAccreditationMissing(creditPayment).then(setAccreditationMissing)
  }, [])

  // The payment was saved but its cash fund / check movement wasn't (e.g. a server error when accrediting it)
  const onRetryAccreditation = async () => {
    if (retryingAccreditation) return
    setRetryingAccreditation(true)
    try {
      await retryAccreditation(creditPayment)
      setAccreditationMissing(false)
      showToast('El cobro se acreditó correctamente.')
    } catch (error) {
      showErrorStrToast(error instanceof Error && error.message === 'El cobro ya está acreditado' ?
        'El cobro ya estaba acreditado.' :
        'No se pudo acreditar el cobro. Intentá de nuevo en unos minutos.')
      setAccreditationMissing(await isAccreditationMissing(creditPayment).catch(() => true))
    } finally {
      setRetryingAccreditation(false)
    }
  }

  const title = describe(TableName.CREDIT_PAYMENTS, creditPayment)
  const crumbs = useGetCrumbs(
    'Cuentas Corrientes',
    RoutePath.CREDIT_ACCOUNTS,
    title
  )

  const initialDebt = useMemo(() => creditPayment.initialDebt || operations.reduce((sum, operation) => sum + getPriceOutline(getTable(operation), operation).total, 0), [operations])
  const paidAmount = useMemo(() => creditPayment.amount, [])
  const paymentType = useMemo(() => paymentTypeLoaders().findPaymentType(creditPayment.paymentTypeId), [])
  const adjustedPaidAmount = useMemo(() => pricesHooks.adjustment.remove(paidAmount, paymentType?.yield || 0), [paidAmount, paymentType])
  const paymentTypeAdjustment = useMemo(() => paidAmount - adjustedPaidAmount, [adjustedPaidAmount, paidAmount])
  const initialCredit = useMemo(() => creditPayment.initialCredit || 0, [])
  const debtToCancel = useMemo(() => initialDebt - initialCredit + paymentTypeAdjustment, [initialDebt, initialCredit, paymentTypeAdjustment])
  const pendingAmount = useMemo(() => paidAmount - debtToCancel, [paidAmount, debtToCancel])

  const fields: DetailFieldProps[] = [
    {
      name: 'creationDate',
      type: FieldType.DATE,
      label: 'Fecha'
    },
    {
      name: 'customerId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMERS,
      label: 'Cliente'
    },
    {
      name: 'status',
      type: FieldType.TEXT,
      label: 'Estado de Pago',
      render: (value?: FieldValue) => <>{Labels.paymentOrderStatusLabel(define(value) as PaymentOrderStatus)}</>
    },
    {
      name: 'operationItems',
      type: FieldType.TABLE,
      render: () => <CreditPaymentOperationItemsTable operationItems={creditPayment.operationItems} />
    },
    {
      name: 'initialDebt',
      type: FieldType.PRICE,
      label: 'Deuda Total',
      render: () => <StaticValue type={FieldType.PRICE} value={initialDebt} />
    },
    {
      name: 'debtToCancel',
      type: FieldType.PRICE,
      label: 'Monto a Cancelar',
      render: () => <StaticValue type={FieldType.PRICE} value={debtToCancel} />
    },
    {
      name: 'paidAmount',
      type: FieldType.PRICE,
      label: 'Monto Recibido',
      render: () => <StaticValue type={FieldType.PRICE} value={paidAmount} bold />
    },
    {
      name: 'pendingAmount',
      type: FieldType.PRICE,
      label: `${pendingAmount > 0 ? 'Crédito a Favor' : 'Saldo Pendiente'}`,
      render: () => <StaticValue type={FieldType.PRICE} value={Math.abs(pendingAmount)} />
    }
  ]
  if (initialCredit > 0) {
    fields.splice(5, 0, {
      name: 'initialCredit',
      type: FieldType.PRICE,
      label: 'Crédito Previo',
      render: () => <StaticValue type={FieldType.PRICE} value={initialCredit} />
    })
  }
  if (paymentTypeAdjustment !== 0) {
    fields.splice(5, 0, {
      name: 'paymentTypeAdjustment',
      type: FieldType.PRICE,
      label: `${paymentTypeAdjustment < 0 ? 'Dto.' : 'Ajuste'} Medio de Pago:`,
      render: () => <StaticValue type={FieldType.PRICE} value={paymentTypeAdjustment} />
    })
  }

  if (creditPayment.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = creditPayment.negativeStateAt ? ` - ${formatDateTime(new Date(creditPayment.negativeStateAt))}` : ''
        return <><CompanyUserName userId={creditPayment.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const onDownloadPaymentReceipt = () => openModal(ModalId.DOWNLOAD_PAYMENT_RECEIPT_PDF)

  const onDownloadReceipt = () => openModal(ModalId.DOWNLOAD_RECEIPT)

  const onUploadReceipt = () => openModal(ModalId.UPLOAD_CREDIT_RECEIPT)
  
  const onArchive = () => openModal(ModalId.DIRECT_ARCHIVE)

  const onAnnul = () => openModal(ModalId.ANNUL_CREDIT_PAYMENT)

  const actions: Action[] = useMemo(() => {
    const actionList: Action[] = []

    if (creditPayment.status === PaymentOrderStatus.PAYED) {
      actionList.push({ label: 'Recibo', icon: 'download', kind: 'tertiary', onClick: onDownloadPaymentReceipt })
    }

    if (creditPayment.receiptUrl) {
      actionList.push({ label: 'Comp. de Pago', icon: 'download', kind: 'tertiary', onClick: onDownloadReceipt })
    } else if (creditPayment.paymentTypeId) {
      actionList.push({ label: 'Subir Comprobante', icon: 'upload', onClick: onUploadReceipt })
    }

    if (!readOnly && accreditationMissing) {
      actionList.push({ label: retryingAccreditation ? 'Acreditando...' : 'Acreditar en Caja', icon: 'arrow-repeat', onClick: onRetryAccreditation })
    }

    if (!readOnly && isArchivable(creditPayment)) {
      actionList.push({ label: 'Archivar Cobro', icon: 'archive-fill', kind: 'quinary', onClick: onArchive })
    }

    if (!readOnly && isAnnullable(creditPayment)) {
      actionList.push({ label: 'Anular Cobro', icon: 'slash-circle', kind: 'secondary', onClick: onAnnul })
    }

    return actionList
  }, [creditPayment.status, creditPayment.receiptUrl, readOnly, creditPayment.archived, accreditationMissing, retryingAccreditation])

  return (<>
    <DetailPanel
      title={title}
      tableName={TableName.CREDIT_PAYMENTS}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='cash-stack'
      readOnly
      actions={actions}
    />
    <DownloadReceiptModal operation={creditPayment} />
    {creditPayment.paymentTypeId && (
      <UploadCreditReceiptFormModal creditPayment={creditPayment} />
    )}
    <DownloadPaymentReceiptPDFModal creditPayment={creditPayment} />
    <DirectArchiveModal
      tableName={TableName.CREDIT_PAYMENTS}
      dataType={creditPayment}
    />
    {!readOnly && isAnnullable(creditPayment) && (
      <AnnulCreditPaymentModal creditPayment={creditPayment} />
    )}
  </>)
}
